import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";

// Email validation schema aligned with server-side RLS policy
const emailSchema = z.string()
  .min(5, "Email too short")
  .max(254, "Email too long")
  .email("Invalid email address")
  .regex(/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/, "Invalid email format");

const WaitlistSection = () => {
  const [email, setEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const {
    toast
  } = useToast();
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast({
        title: "Email required",
        description: "Please enter your email address.",
        variant: "destructive"
      });
      return;
    }

    // Validate email using zod schema aligned with server-side validation
    const normalizedEmail = email.toLowerCase().trim();
    const validationResult = emailSchema.safeParse(normalizedEmail);
    
    if (!validationResult.success) {
      toast({
        title: "Invalid email",
        description: validationResult.error.errors[0].message,
        variant: "destructive"
      });
      return;
    }
    setIsLoading(true);
    const { error } = await supabase.from("waitlist").insert({
      email: normalizedEmail,
      source: "homepage"
    });
    if (error) {
      if (error.code === "23505") {
        toast({
          title: "Already registered",
          description: "This email is already on the waitlist!"
        });
        setIsSubmitted(true);
      } else {
        toast({
          title: "Something went wrong",
          description: "Please try again later.",
          variant: "destructive"
        });
      }
      setIsLoading(false);
      return;
    }

    // Send confirmation email
    try {
      await supabase.functions.invoke("send-confirmation", {
        body: {
          email: normalizedEmail
        }
      });
      console.log("Confirmation email sent successfully");
    } catch (emailError) {
      console.error("Failed to send confirmation email:", emailError);
      // Don't block signup if email fails
    }
    setIsSubmitted(true);
    setIsLoading(false);
  };
  return <section id="waitlist" aria-labelledby="waitlist-heading" className="lg:py-32 relative py-[50px] overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-full">
        <div className="max-w-2xl mx-auto text-center">
          <h3 id="waitlist-heading" className="text-lg md:text-xl font-normal mb-4 text-foreground">
            Ready to earn from real estate?
          </h3>
          <p className="text-foreground/70 text-sm mb-4">
            The future of on-chain property income is taking shape.
          </p>
          <p className="text-foreground/70 text-sm mb-12">
            Get early access.
          </p>

          {isSubmitted ? <div className="inline-flex items-center gap-3 px-5 py-4 rounded-lg bg-card border border-border/50 animate-fade-up">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <Check className="w-4 h-4 text-primary" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-foreground">You're in.</p>
                <p className="text-xs text-foreground/70">Early access is confirmed.</p>
                <p className="text-xs text-foreground/50 mt-1">No spam. Only milestones.</p>
              </div>
            </div> : <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-sm mx-auto" aria-label="Join waitlist form">
              <label htmlFor="waitlist-email" className="sr-only">Email address</label>
              <Input id="waitlist-email" type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} className="h-10 bg-card border-border/50 focus:border-primary/50 text-foreground placeholder:text-foreground/40 text-base" aria-required="true" autoComplete="email" />
              <Button type="submit" variant="hero" size="default" disabled={isLoading} className="shrink-0 rounded-full px-6">
                {isLoading ? "Joining..." : "Join Waitlist"}
              </Button>
            </form>}
        </div>
      </div>
    </section>;
};
export default WaitlistSection;